import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  inject,
  computed,
} from "@angular/core";
import { FormArray, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Subscription } from "rxjs";
import { IConditionGroupForm, IConditionItemForm } from "src/app/shared/interfaces";

import { ConfirmDialogComponent } from "@components/shared";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { IconConstants, TextConstants } from "@shared/constants";
import { ConfirmDialogResponseEnum, RulesetBooleanOperatorEnum } from "@shared/enums";

enum MatchType {
  ALL,
  ANY,
}

@Component({
  standalone: false,
  selector: "app-ruleset-criteria-group",
  templateUrl: "./ruleset-criteria-group.component.html",
  styleUrl: "./ruleset-criteria-group.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RulesetCriteriaGroupComponent implements OnInit, OnDestroy {
  formGroup = input.required<FormGroup<IConditionGroupForm>>();

  propertyOptions = input.required<InputSelectOption[]>();

  locationTypeOptions = input.required<InputSelectOption[]>();

  countryOptions = input.required<InputSelectOption[]>();

  shouldBeIndented = input.required<boolean>();

  locationOptions = input<InputSelectOption[]>();

  booleanOperator = input<string>(null);

  isReadOnly = input<boolean>(false);

  remove = output<void>();

  addCriteria = output<FormGroup>();

  convertCriteriaItemToGroup = output<number>();

  removeCriteria = output<{ formGroup: FormGroup; index: number }>();

  selectedMatchTypeIndex = signal(MatchType.ALL);

  private readonly dialog = inject(MatDialog);

  private subscriptions = new Subscription();

  readonly matchType = {
    [MatchType.ALL]: RulesetBooleanOperatorEnum.AND,
    [MatchType.ANY]: RulesetBooleanOperatorEnum.OR,
  };

  readonly translations = {
    matchLabel: $localize`:@@ruleset.match:Match`,
    all: $localize`:@@shared.all:All`,
    any: $localize`:@@shared.any:Any`,
    deleteGroup: $localize`:@@ruleset.deletecriteriagroup:Delete criteria group`,
    addCriteria: $localize`:@@ruleset.addcriteria:Add criteria`,
    addCriteriaGroup: $localize`:@@ruleset.addcriteriagroup:Add criteria group`,
  };

  booleanOperatorText = computed(() => {
    return this.booleanOperator() === RulesetBooleanOperatorEnum.OR
      ? $localize`:@@shared.or:Or`
      : $localize`:@@shared.and:And`;
  });

  ngOnInit(): void {
    this.setSelectedMatchTypeIndex(this.formGroup().get("booleanOperator").value);

    this.subscriptions.add(
      this.formGroup()
        .get("booleanOperator")
        .valueChanges.subscribe((value) => {
          this.setSelectedMatchTypeIndex(value);
        }),
    );

    this.subscriptions.add(
      this.formGroup()
        .get("itemForms")
        .valueChanges.subscribe((conditionTypeFormValues) => {
          if (conditionTypeFormValues.length === 1) {
            this.formGroup().get("booleanOperator").setValue(RulesetBooleanOperatorEnum.AND);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setSelectedMatchTypeIndex(value: string) {
    this.selectedMatchTypeIndex.set(
      value === RulesetBooleanOperatorEnum.AND ? MatchType.ALL : MatchType.ANY,
    );
  }

  get conditionTypeFormControls() {
    const itemForms = this.formGroup().get("itemForms") as FormArray<FormGroup<IConditionItemForm>>;

    return itemForms.controls;
  }

  onMatchTypeChange(index: MatchType) {
    const booleanOperator =
      index === MatchType.ALL ? RulesetBooleanOperatorEnum.AND : RulesetBooleanOperatorEnum.OR;

    this.formGroup().patchValue({ booleanOperator });
  }

  onRemove() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: $localize`:@@ruleset.deletecriteriagroup:Delete criteria group`,
        contentText: $localize`:@@ruleset.deletecriteriagroup.confirm:Are you sure you want to delete the criteria group?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.DELETE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe((result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.remove.emit();
      }
    });
  }

  onAddCriteria() {
    this.addCriteria.emit(this.formGroup());
  }

  onRemoveCriteria(criteriaItemIndex: number) {
    (this.formGroup().get("itemForms") as FormArray).removeAt(criteriaItemIndex);
  }
}
